import React, { useEffect, useState } from 'react';
import { Form, Button, Col } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import Switch from 'react-switch';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { axiosPut } from '../../utils/axiosPut';
import { windowScrollToTop } from '../../utils/windowScrollToTop';
import { cmSeLabel } from '../../utils/constants.js';

import PageTitle from '../layouts/PageTitle';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import useNavigationPrompt from '../hooks/useNavigationPrompt';
import { devError } from '../../utils/devLog';

const Committee = () => {
  const { activeFellowsArray, sectionalCommittees, usersArray } =
    useStaticCollections();
  const { committeeArray, triggerCommitteeRefresh } = useAdminCollections();
  const [notification, setNotification] = useState(null);
  const [toggleStates, setToggleStates] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, setValue, getValues, handleSubmit } = useForm();

  const updatedSectionalCommittees = {
    ...sectionalCommittees,
    14: cmSeLabel,
  };

  const getCurrentlyAssignedSCUsers = () => {
    const assignedUserIds = new Set();

    committeeArray.forEach((committee) => {
      if (committee?.attributes?.committeeChair?.data?.id) {
        assignedUserIds.add(committee.attributes.committeeChair.data.id);
      }

      if (committee?.attributes?.committeeMembers?.data) {
        committee.attributes.committeeMembers.data.forEach((member) => {
          assignedUserIds.add(member.id);
        });
      }
    });

    return assignedUserIds;
  };

  const checkExistingCouncilObserver = (userId, currentCommitteeId) => {
    const existingCommittees = [];

    for (const committee of committeeArray) {
      if (committee.id !== parseInt(currentCommitteeId)) {
        const existingObservers =
          committee.attributes.councilObservers?.data || [];
        if (existingObservers.some((observer) => observer.id === userId)) {
          existingCommittees.push({
            id: committee.id,
            isUnsaved: false,
          });
        }
      }
    }

    const formValues = getValues();
    for (const [fieldName, selectedOptions] of Object.entries(formValues)) {
      if (fieldName.includes('council-observer') && selectedOptions) {
        const committeeId = fieldName.match(/\d+/)[0];
        if (committeeId !== String(currentCommitteeId)) {
          const isSelected = selectedOptions.some(
            (option) => option.value === userId,
          );
          if (isSelected) {
            const alreadyAdded = existingCommittees.some(
              (c) => c.id === parseInt(committeeId),
            );
            if (!alreadyAdded) {
              existingCommittees.push({
                id: parseInt(committeeId),
                isUnsaved: true,
              });
            }
          }
        }
      }
    }

    return existingCommittees.length > 0 ? existingCommittees : null;
  };

  const getCouncilObserverSelectOptions = () => {
    const currentlyAssignedSCUsers = getCurrentlyAssignedSCUsers();

    return activeFellowsArray
      .map((item) => {
        if (item.id) {
          const user = usersArray.find((users) => users.fellowId === item.id);
          if (user) {
            if (currentlyAssignedSCUsers.has(user.id)) {
              return null;
            }

            return {
              value: user.id,
              label: item.attributes.label,
            };
          }
        }
        return null;
      })
      .filter(Boolean);
  };

  const handleCouncilObserverChange = (
    selectedOption,
    fieldName,
    committeeId,
    actionMeta,
  ) => {
    handleFieldChange(selectedOption, fieldName);

    if (actionMeta?.action === 'select-option' && actionMeta?.option) {
      const newSelection = actionMeta.option;
      const existingCommittees = checkExistingCouncilObserver(
        newSelection.value,
        committeeId,
      );

      if (existingCommittees) {
        const committeeNames = existingCommittees.map((committee) => {
          const name =
            updatedSectionalCommittees[committee.id] ||
            `Committee ${committee.id}`;
          return committee.isUnsaved ? `${name} (unsaved)` : name;
        });

        const messageText =
          existingCommittees.length === 1
            ? `${newSelection.label} is already a Council Observer for ${committeeNames[0]}`
            : `${newSelection.label} is already a Council Observer for: ${committeeNames.join(', ')}.`;

        setNotificationTitle('Council Observer Already Assigned');
        setNotificationMessage(messageText);

        setTimeout(() => {
          setNotificationTitle('');
          setNotificationMessage('');
        }, 5000);
      }
    }
  };

  const processMembers = (membersData) => {
    const processed = [];
    membersData.forEach((member) => {
      const selectedFellow = activeFellowsArray.find(
        (fellow) => fellow.id === parseInt(member.attributes.fellowId),
      );
      if (selectedFellow) {
        processed.push({
          value: member.id,
          label: selectedFellow.attributes.label,
        });
      }
      if (member.attributes.nonAasRefereeId !== null) {
        processed.push({
          value: member.id,
          label: member.attributes.username,
        });
      }
    });
    return processed;
  };

  useEffect(() => {
    (async () => {
      try {
        const initialToggleStates = {};
        committeeArray.forEach((item) => {
          initialToggleStates[item?.id] =
            item?.attributes?.isShortlistingFinalised;
        });
        setToggleStates(initialToggleStates);

        committeeArray.forEach(({ id, attributes }) => {
          if (attributes.committeeChair.data) {
            const selectedFellow = activeFellowsArray.find(
              (fellow) =>
                fellow.id ===
                parseInt(attributes.committeeChair?.data?.attributes.fellowId),
            );
            if (selectedFellow) {
              setValue(`committee-chair-${id}`, {
                value: attributes.committeeChair.data.id,
                label: selectedFellow.attributes.label,
              });
            }
          }

          const committeeMembers = processMembers(
            attributes.committeeMembers.data,
          );
          setValue(`committee-member-${id}`, committeeMembers);

          if (attributes.councilObservers?.data) {
            const councilObservers = processMembers(
              attributes.councilObservers.data,
            );
            setValue(`council-observer-${id}`, councilObservers);
          }
        });
      } catch (error) {
        devError('Error in Committee:', error);
      }
    })();
  }, [committeeArray, setValue, activeFellowsArray]);

  const onToggleChange = (id) => {
    setToggleStates((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    setIsDirty(false);
  }, [committeeArray]);

  useEffect(() => {
    if (notificationTitle === 'Council Observer Already Assigned') {
      return;
    }
    if (isDirty) {
      setNotificationTitle('Unsaved Changes');
      setNotificationMessage(
        'To save changes, you are required to press the submit button at the bottom of this page.',
      );
    } else {
      setNotificationTitle('');
      setNotificationMessage('');
    }
  }, [isDirty, notificationTitle]);

  const navigationPrompt = useNavigationPrompt(
    isDirty,
    'You have unsaved changes. Are you sure you want to leave this page?',
  );

  const onSubmit = async (formData, e) => {
    e.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const committeeUpdates = {};

      for (const [fieldName, selectedOption] of Object.entries(formData)) {
        const committeeId = fieldName.match(/\d+/)[0];

        if (!committeeUpdates[committeeId]) {
          committeeUpdates[committeeId] = {};
        }

        if (fieldName.includes('committee-chair') && selectedOption) {
          committeeUpdates[committeeId].committeeChair = {
            id: selectedOption.value,
          };
        }

        if (fieldName.includes('committee-member') && selectedOption) {
          committeeUpdates[committeeId].committeeMembers = selectedOption.map(
            (option) => ({ id: option.value }),
          );
        }

        if (fieldName.includes('council-observer') && selectedOption) {
          committeeUpdates[committeeId].councilObservers = selectedOption.map(
            (option) => ({ id: option.value }),
          );
        }
      }

      for (const [committeeId, updateData] of Object.entries(
        committeeUpdates,
      )) {
        const toggleState = toggleStates[committeeId];
        if (toggleState !== undefined) {
          updateData.isShortlistingFinalised = toggleState ?? false;
        }

        await axiosPut(`update-committee/${committeeId}`, {
          data: updateData,
        });
      }

      for (const [committeeId, isShortlistingFinalised] of Object.entries(
        toggleStates,
      )) {
        if (!committeeUpdates[committeeId]) {
          await axiosPut(`update-committee/${committeeId}`, {
            data: { isShortlistingFinalised: isShortlistingFinalised ?? false },
          });
        }
      }

      triggerCommitteeRefresh();

      setNotification('Updated');
      setTimeout(() => {
        setNotification('');
      }, 5000);
      windowScrollToTop();
    } catch (error) {
      devError('Error updating committees:', error);
    } finally {
      setIsSubmitting(false);
    }
    setIsDirty(false);
  };

  const selectOptions = activeFellowsArray
    .map((item) => {
      if (item.id) {
        const user = usersArray.find((users) => users.fellowId === item.id);
        if (user) {
          return {
            value: user.id,
            label: item.attributes.label,
          };
        }
      }
      return null;
    })
    .filter(Boolean);

  const handleFieldChange = (selectedOption, fieldName) => {
    setValue(fieldName, selectedOption);
    setIsDirty(true);
  };

  const baseSelectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      borderRadius: '0.5rem',
      borderColor: '#B3B3B3',
    }),
  };

  const chairSelectStyles = {
    control: (baseStyles) => ({
      ...baseSelectStyles.control(baseStyles),
      height: `3rem`,
    }),
  };

  return (
    <>
      {navigationPrompt}
      <PageTitle
        activeMenu="Committees"
        motherMenu="Home"
        notificationMessage={notificationMessage || notification}
        notificationTitle={notificationTitle}
      />
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Committees</h4>
        </div>
        <div className="card-body text-left">
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              {Object.entries(updatedSectionalCommittees).map(
                ([id, label], index) => (
                  <React.Fragment key={`committee-${id}`}>
                    {index > 0 && <hr className="my-4" />}
                    <Col xs={12}>
                      <h5 className="mb-xl-4 d-flex justify-content-between align-items-center">
                        {label}
                        <div className="d-flex align-items-center">
                          <span className="me-2 fw-normal">
                            Shortlisting finalised
                          </span>
                          <div
                            data-tooltip-id={`tooltip-${id}`}
                            data-tooltip-content={`${
                              toggleStates[id]
                                ? 'Reminders will be sent to referees of shortlisted candidates only'
                                : 'Reminders will be sent to referees of all candidates'
                            }`}
                          >
                            <Switch
                              onChange={() => onToggleChange(id)}
                              checked={toggleStates[id] || false}
                              onColor="#4cd964"
                              offColor="#e5e5ea"
                              handleDiameter={20}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              height={24}
                              width={48}
                            />
                          </div>
                          <Tooltip id={`tooltip-${id}`} place="top" />
                        </div>
                      </h5>
                    </Col>
                    <Col key={`chair-${id}`} xs={12} md={6}>
                      <div className="mb-xl-5">
                        <label
                          htmlFor={`committee-chair-${id}`}
                          className="form-label"
                        >
                          Chair
                        </label>
                        <Controller
                          name={`committee-chair-${id}`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={selectOptions}
                              onChange={(selectedOption) => {
                                handleFieldChange(
                                  selectedOption,
                                  `committee-chair-${id}`,
                                );
                              }}
                              styles={chairSelectStyles}
                              className="mt-1"
                            />
                          )}
                        />
                      </div>
                    </Col>
                    <Col key={`member-observer-${id}`} xs={12} md={6}>
                      <div className="mb-xl-5">
                        <label
                          htmlFor={`committee-member-${id}`}
                          className="form-label"
                        >
                          Member
                        </label>
                        <Controller
                          name={`committee-member-${id}`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={selectOptions}
                              onChange={(selectedOption) => {
                                handleFieldChange(
                                  selectedOption,
                                  `committee-member-${id}`,
                                );
                              }}
                              styles={baseSelectStyles}
                              isMulti
                              className="mt-1"
                            />
                          )}
                        />
                      </div>
                      <div className="mb-xl-5">
                        <label
                          htmlFor={`council-observer-${id}`}
                          className="form-label"
                        >
                          Council Observers
                        </label>
                        <Controller
                          name={`council-observer-${id}`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={getCouncilObserverSelectOptions()}
                              onChange={(selectedOption, actionMeta) => {
                                handleCouncilObserverChange(
                                  selectedOption,
                                  `council-observer-${id}`,
                                  id,
                                  actionMeta,
                                );
                              }}
                              styles={baseSelectStyles}
                              isMulti
                              className="mt-1"
                              placeholder="Select council observers..."
                            />
                          )}
                        />
                      </div>
                    </Col>
                  </React.Fragment>
                ),
              )}
            </div>
            <div className="text-end">
              <Button
                variant="success"
                type="submit"
                className="standard-width-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Submit'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default Committee;
